import React, { memo, useState, useCallback } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Calendar as CalendarIcon, Mail, Users, X } from 'lucide-react';
import { toast } from 'sonner';
import { contactsAPI, emailIntegrationAPI } from '../../api';

const MeetingDialog = memo(({ 
  open, 
  onOpenChange, 
  contactId,
  contact,
  teamUsers = [],
  onSuccess 
}) => {
  const [form, setForm] = useState({
    title: '',
    start_time: '',
    end_time: '',
    meeting_link: '',
    attendees: []
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (!form.start_time || !form.end_time) {
      toast.error('Please select start and end time');
      return;
    }
    
    try {
      setLoading(true);
      await contactsAPI.createMeeting(contactId, {
        ...form,
        contact_id: contactId,
        start_time: new Date(form.start_time).toISOString(),
        end_time: new Date(form.end_time).toISOString(),
        attendees: form.attendees || []
      });
      toast.success('Meeting scheduled' + (form.attendees.length > 0 ? ` with ${form.attendees.length} team member(s)` : ''));
      
      // Reset form
      setForm({
        title: '',
        start_time: '',
        end_time: '',
        meeting_link: '',
        attendees: []
      });
      
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      toast.error('Failed to create meeting');
    } finally {
      setLoading(false);
    }
  }, [form, contactId, onOpenChange, onSuccess]);

  const handleClose = useCallback(() => {
    setForm({
      title: '',
      start_time: '',
      end_time: '',
      meeting_link: '',
      attendees: []
    });
    onOpenChange(false);
  }, [onOpenChange]);

  const addAttendee = useCallback((userId) => {
    if (userId && !form.attendees.includes(userId)) {
      setForm(prev => ({ ...prev, attendees: [...prev.attendees, userId] }));
    }
  }, [form.attendees]);

  const removeAttendee = useCallback((userId) => {
    setForm(prev => ({ ...prev, attendees: prev.attendees.filter(id => id !== userId) }));
  }, []);

  const handleSendBookingLink = useCallback(async () => {
    if (!contact?.email) {
      toast.error('Contact has no email address');
      return;
    }
    try {
      await emailIntegrationAPI.sendEmail({
        to_email: contact.email,
        subject: 'Schedule a Meeting with Us',
        body: `Hi ${contact.first_name},\n\nPlease use this link to schedule a meeting at a time that works best for you:\n\n${window.location.origin}/book\n\nLooking forward to speaking with you!\n\nBest regards`,
        contact_id: contact.id
      });
      toast.success(`Booking link sent to ${contact.email}`);
      onOpenChange(false);
    } catch (error) {
      if (error.response?.status === 400 && error.response?.data?.detail?.includes('not connected')) {
        toast.error('Email not connected. Go to Email Settings to connect your Outlook.');
      } else {
        toast.error('Failed to send email. Please copy the link and send manually.');
      }
    }
  }, [contact, onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarIcon className="h-5 w-5 text-blue-600" />
            Schedule Meeting with {contact?.first_name}
          </DialogTitle>
          <DialogDescription>
            Schedule a meeting or send a self-booking link
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Title</Label>
            <Input
              value={form.title}
              onChange={(e) => setForm(prev => ({ ...prev, title: e.target.value }))}
              placeholder="e.g., Sales Consultation, Product Demo"
              required
              data-testid="meeting-title-input"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Start</Label>
              <Input
                type="datetime-local"
                value={form.start_time || ''}
                onChange={(e) => setForm(prev => ({ ...prev, start_time: e.target.value }))}
                required
                data-testid="meeting-start-input"
              />
            </div>
            <div className="space-y-2">
              <Label>End</Label>
              <Input
                type="datetime-local"
                value={form.end_time || ''}
                onChange={(e) => setForm(prev => ({ ...prev, end_time: e.target.value }))}
                required
                data-testid="meeting-end-input"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Location / Meeting Link</Label>
            <Input
              value={form.meeting_link}
              onChange={(e) => setForm(prev => ({ ...prev, meeting_link: e.target.value }))}
              placeholder="Zoom/Meet link or address"
              data-testid="meeting-link-input"
            />
          </div>
          
          {/* Invite Team Members */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Invite Team Members
            </Label>
            <Select onValueChange={addAttendee}>
              <SelectTrigger data-testid="meeting-invite-select">
                <SelectValue placeholder="Add team member..." />
              </SelectTrigger>
              <SelectContent>
                {teamUsers.filter(u => !form.attendees.includes(u.id)).map(user => (
                  <SelectItem key={user.id} value={user.id}>
                    {user.full_name || user.email} ({user.role})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {form.attendees.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {form.attendees.map(userId => {
                  const user = teamUsers.find(u => u.id === userId);
                  return (
                    <Badge key={userId} variant="secondary" className="flex items-center gap-1">
                      {user?.full_name || user?.email || userId}
                      <X 
                        className="h-3 w-3 cursor-pointer hover:text-red-500" 
                        onClick={() => removeAttendee(userId)}
                      />
                    </Badge>
                  );
                })}
              </div>
            )}
          </div>
          
          {/* Self-Service Booking Link Option */}
          <div className="p-4 bg-blue-50 rounded-lg space-y-3">
            <div className="flex items-center gap-2 text-blue-700">
              <CalendarIcon className="h-4 w-4" />
              <span className="font-medium text-sm">Or let the customer schedule</span>
            </div>
            <p className="text-sm text-blue-600">
              Share this link with {contact?.first_name} so they can pick a time that works for them:
            </p>
            <div className="flex gap-2">
              <Input 
                readOnly 
                value={`${window.location.origin}/book`}
                className="bg-white text-sm"
              />
              <Button 
                type="button" 
                variant="outline" 
                size="sm"
                onClick={() => {
                  navigator.clipboard.writeText(`${window.location.origin}/book`);
                  toast.success('Booking link copied to clipboard!');
                }}
                data-testid="copy-booking-link-btn"
              >
                Copy
              </Button>
            </div>
            <div className="flex gap-2">
              <Button 
                type="button" 
                variant="outline" 
                size="sm"
                className="flex-1"
                onClick={handleSendBookingLink}
                data-testid="email-booking-link-btn"
              >
                <Mail className="h-4 w-4 mr-2" />
                Email Link to {contact?.first_name}
              </Button>
            </div>
          </div>
          
          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>Cancel</Button>
            <Button type="submit" disabled={loading} data-testid="meeting-submit-btn">
              {loading ? 'Scheduling...' : 'Schedule Meeting'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
});

MeetingDialog.displayName = 'MeetingDialog';

export default MeetingDialog;
